"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Search,
  Wrench,
  LifeBuoy,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { StructuredData } from "@/components/common";
import { AnthropicPartnerBadge } from "@/components/ui/anthropic-partner-badge";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { renderAccent } from "@/lib/render-accent";
import AgentRescueForm from "./AgentRescueForm";

const HERO = "/images/solutions/agent-rescue-hero.jpg";

const AUDIT_CHECKS: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: Check, title: "Does it actually do the task?", body: "We score it with Agent GPA (Goal / Plan / Action) against real cases — does it reach the right outcome, or just look busy?" },
  { icon: ShieldCheck, title: "Is it safe and governed?", body: "Guardrails, prompt-injection resistance, what it's allowed to touch, and whether sensitive data is handled PDPA/PDPC-aligned." },
  { icon: Search, title: "Is it reliable and observable?", body: "Does it break on edge cases, retry safely, and log what it did — or fail silently with no trail to debug?" },
  { icon: Wrench, title: "Is it maintainable and sane on cost?", body: "Code, docs and tests you can actually build on, model choices that aren't burning money, and no vendor lock-in." },
];

type Currency = "MYR" | "SGD";

const STEPS: {
  icon: LucideIcon;
  tag: string;
  title: string;
  priceLabel?: string;
  price?: Record<Currency, string>;
  body: string;
}[] = [
  { icon: Search, tag: "Step 1", title: "Free agent audit", priceLabel: "Free", body: "Send us the agent and what's going wrong. We assess it and give you an honest verdict: rectify or rebuild — and why." },
  { icon: Wrench, tag: "Step 2", title: "Handover & remediation", price: { MYR: "RM 2,000–5,000", SGD: "S$1,000–3,000" }, body: "A one-off takeover: we assume ownership, stabilise the agent, and hand you a remediation plan with the fixes in place — priced to how much is broken." },
  { icon: LifeBuoy, tag: "Step 3", title: "Maintenance & support", priceLabel: "Monthly", body: "An ongoing retainer so it holds up under real load — monitoring, evals on every change, and a team that answers." },
];

const REASONS: { title: string; body: string }[] = [
  { title: "We build on Claude, properly", body: "Certified in the Claude Partner Network. We build on the Claude Agent SDK + MCP as Anthropic designed them, not a fragile wrapper." },
  { title: "We measure before we trust", body: "Agent GPA evals against a golden set. If it can't beat a human baseline, we tell you — and we don't ship it either." },
  { title: "Honest verdicts", body: "If a rebuild is cheaper and safer than a rescue, we say so. The audit is a real assessment, not a sales funnel." },
];

const FAQS = [
  { question: "What does the free audit tell me?", answer: "A plain verdict in one of three directions: your agent is fixable as-is, it's fixable but needs an ongoing monthly maintenance top-up to hold up under real load, or it's better rebuilt from a sound foundation. Either way you get the reasoning, so you can decide with your eyes open." },
  { question: "How much does it cost in Malaysia and Singapore?", answer: "The audit is free. If we take over the agent, handover is a one-off RM 2,000–5,000 in Malaysia (S$1,000–3,000 in Singapore) depending on how much is broken; ongoing maintenance is a monthly retainer scoped to your agent's load — the audit tells you whether you'll need it. We quote after the audit — no surprises." },
  { question: "Is AI Agent Rescue available in both Malaysia and Singapore?", answer: "Yes — we work with businesses across Malaysia and Singapore, quote in RM or SGD, and assess your agent the same way regardless of where or on what it was built." },
  { question: "What if the agent can't be saved?", answer: "We'll tell you plainly. Sometimes a rebuild is cheaper and safer than patching a broken foundation — and we'd rather you hear that up front than pay to prop up something that won't hold." },
  { question: "What do you need from us for the audit?", answer: "Whatever you have: access to the agent or a demo, a description of what it should do, and any code, prompts, or reports. The more we see, the sharper the verdict. NDAs are fine." },
  { question: "Our vendor built it on something other than Claude — can you still help?", answer: "Usually yes. We assess the agent on its merits regardless of stack; if a move to Claude is the right call, that becomes part of the remediation plan." },
  { question: "How fast is the audit?", answer: "We aim to come back with an honest read within one business day of receiving your details." },
];

export default function AgentRescuePage({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const prefersReducedMotion = useReducedMotion();
  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.55, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });
  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  // MYR renders first (no hydration mismatch); switch to SGD for Singapore visitors.
  const [currency, setCurrency] = useState<Currency>("MYR");
  useEffect(() => {
    try {
      if (Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Singapore") {
        setCurrency("SGD");
      }
    } catch {
      /* keep MYR default */
    }
  }, []);

  return (
    <div className="bg-background">
      <StructuredData
        type="faq"
        faqs={FAQS.map((f) => ({ question: f.question, answer: f.answer }))}
      />

      {/* Hero */}
      <section className="bg-background pt-24 pb-12 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div {...reveal(0)}>
              <nav className="mb-5 font-mono text-xs uppercase tracking-[0.25em] text-neutral-muted">
                <Link href={getLocalizedPathname("/", locale)} className="transition-colors hover:text-primary">
                  Home
                </Link>
                <span className="mx-2" aria-hidden>/</span>
                <span className="text-neutral-emphasis">{t(locale, "AI Agent Rescue")}</span>
              </nav>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary">
                {t(locale, "AI Agent Rescue")}
              </p>
              <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-neutral-emphasis md:text-5xl">
                {renderAccent(t(locale, "Your AI agent doesn't work? We'll tell you if it's [[fixable]]."))}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted">
                {t(locale, "A vendor built you an AI agent and it's not delivering — wrong answers, breaking, abandoned, or nobody's sure. Send it to us for a free audit and an honest verdict: rectify or rebuild.")}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Link
                  href="#audit"
                  data-track-event="cta_click"
                  data-track-location="agent_rescue_hero"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  {t(locale, "Get a free audit")}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <Link
                  href={getLocalizedPathname("/solutions/agent-use-cases/", locale)}
                  className="group inline-flex items-center justify-center gap-2 text-sm font-semibold text-neutral-emphasis transition hover:text-primary"
                >
                  {t(locale, "See how we build agents")}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
                </Link>
              </div>
            </motion.div>

            <motion.div {...reveal(0.12)} className="relative">
              <div className="overflow-hidden rounded-3xl border border-border shadow-lg">
                <Image src={HERO} alt={t(locale, "AI Agent Rescue — fixing a broken AI agent")} width={1536} height={1024} priority className="h-full w-full object-cover" />
              </div>
              <AnthropicPartnerBadge variant="hero" className="absolute bottom-4 left-4 z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The problem */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
            {t(locale, "Most failed agents fail the same way")}
          </motion.h2>
          <motion.p {...inView} className="mt-4 text-base leading-relaxed text-neutral-muted">
            {t(locale, "It demoed well, so it shipped. Then reality hit: it gives confidently wrong answers, breaks on anything unusual, was never evaluated, has no documentation, quietly burns money on the wrong model — and the vendor has moved on. You're left with something that looks like an AI agent but can't be trusted with real work.")}
          </motion.p>
        </div>
      </section>

      {/* What the audit checks */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">{t(locale, "The free audit")}</p>
            <h2 className="font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
              {t(locale, "What we check — and the verdict you get")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-muted">
              {t(locale, "We assess your agent against the same bar we'd hold our own builds to, then tell you plainly which of three it is: fixable as-is, fixable but needs a monthly maintenance top-up to hold the load, or better rebuilt — with the reasoning either way.")}
            </p>
          </motion.div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {AUDIT_CHECKS.map(({ icon: Icon, title, body }, i) => (
              <motion.div key={title} {...inView} transition={{ ...inView.transition, delay: i * 0.05 }} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold text-neutral-emphasis">{t(locale, title)}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-muted">{t(locale, body)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-step offer / pricing */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <motion.h2 {...inView} className="max-w-3xl font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
              {t(locale, "How the rescue works")}
            </motion.h2>
            <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm" role="group" aria-label={t(locale, "Currency")}>
              {(["MYR", "SGD"] as Currency[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  aria-pressed={currency === c}
                  className={`rounded-full px-3.5 py-1 font-medium transition ${
                    currency === c
                      ? "bg-primary text-primary-foreground"
                      : "text-neutral-muted hover:text-neutral-emphasis"
                  }`}
                >
                  {c === "MYR" ? "RM" : "SGD"}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, tag, title, priceLabel, price, body }, i) => (
              <motion.div key={title} {...inView} transition={{ ...inView.transition, delay: i * 0.06 }} className="flex h-full flex-col rounded-2xl border border-border bg-card p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <p className="mt-4 font-mono text-xs uppercase tracking-[0.16em] text-neutral-muted">{t(locale, tag)}</p>
                <h3 className="mt-1 font-display text-lg font-semibold text-neutral-emphasis">{t(locale, title)}</h3>
                <p className="mt-1 font-display text-2xl font-bold text-primary">
                  {priceLabel ? t(locale, priceLabel) : price![currency]}
                </p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-neutral-muted">{t(locale, body)}</p>
              </motion.div>
            ))}
          </div>
          <motion.p {...inView} className="mt-6 max-w-2xl text-xs leading-relaxed text-neutral-muted">
            {t(locale, "Indicative, shown in RM or SGD. The one-off handover runs RM 2,000–5,000 in Malaysia (S$1,000–3,000 in Singapore) depending on how much is broken; monthly support is scoped to your agent's load. We quote after the free audit — no online checkout, no surprises.")}
          </motion.p>
        </div>
      </section>

      {/* Why us */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2 {...inView} className="max-w-3xl font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
            {t(locale, "Why hand it to Anchor Sprint")}
          </motion.h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {REASONS.map(({ title, body }, i) => (
              <motion.div key={title} {...inView} transition={{ ...inView.transition, delay: i * 0.05 }} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-base font-semibold text-neutral-emphasis">{t(locale, title)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-muted">{t(locale, body)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Intake form */}
      <section id="audit" className="scroll-mt-24 bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-8">
            <h2 className="font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
              {t(locale, "Get your free agent audit")}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-neutral-muted">
              {t(locale, "Tell us what's going wrong. We'll come back with an honest read — usually within one business day.")}
            </p>
          </motion.div>
          <motion.div {...inView} className="rounded-3xl border border-border bg-card p-6 sm:p-8">
            <AgentRescueForm locale={locale} />
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 font-display text-2xl font-semibold text-neutral-emphasis sm:text-3xl">
            {t(locale, "Common questions")}
          </h2>
          <dl className="space-y-6">
            {FAQS.map((f) => (
              <div key={f.question}>
                <dt className="font-display text-base font-semibold text-neutral-emphasis lg:text-lg">
                  {t(locale, f.question)}
                </dt>
                <dd className="mt-1.5 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, f.answer)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
}
